import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import AuthenticationGuard from '../../components/ui/AuthenticationGuard';
import SmartLoadingBuffer from '../../components/ui/SmartLoadingBuffer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import useAuth from '../../hooks/useAuth';
import { fetchHabitsWithLogs } from '../../services/habitsService';
import { fetchKnowledgeEntries } from '../../services/knowledgeService';

const formatTimestamp = (isoString) => {
  try {
    return new Date(isoString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (error) {
    return isoString;
  }
};

const TestingPage = () => {
  const { user, initializing } = useAuth();
  const [habits, setHabits] = useState([]);
  const [habitLogs, setHabitLogs] = useState([]);
  const [knowledgeEntries, setKnowledgeEntries] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const screenShareSupported = useMemo(
    () => typeof navigator !== 'undefined' && Boolean(navigator?.mediaDevices?.getDisplayMedia),
    []
  );

  const [enableScreenShare, setEnableScreenShare] = useState(screenShareSupported);
  const [completed, setCompleted] = useState(() => new Set());
  const [secureSessionActive, setSecureSessionActive] = useState(false);
  const [sessionSubject, setSessionSubject] = useState('');
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [subjectOption, setSubjectOption] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [fullscreenActive, setFullscreenActive] = useState(false);
  const [pointerLocked, setPointerLocked] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [sessionError, setSessionError] = useState(null);
  const [sessionPending, setSessionPending] = useState(false);

  const loadWorkspaceData = useCallback(async () => {
    if (!user?.id) {
      setHabits([]);
      setHabitLogs([]);
      setKnowledgeEntries([]);
      setIsLoadingData(false);
      return;
    }

    setIsLoadingData(true);
    setLoadError(null);

    try {
      const [{ habits: fetchedHabits, logs }, knowledge] = await Promise.all([
        fetchHabitsWithLogs(user.id),
        fetchKnowledgeEntries(user.id),
      ]);
      setHabits(fetchedHabits ?? []);
      setHabitLogs(logs ?? []);
      setKnowledgeEntries(knowledge ?? []);
    } catch (error) {
      console.error('Failed to load testing data', error);
      setLoadError(error);
    } finally {
      setIsLoadingData(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!initializing) {
      loadWorkspaceData();
    }
  }, [initializing, loadWorkspaceData]);

  const manualChecklist = useMemo(() => {
    const flows = [];

    habits.slice(0, 2).forEach((habit) => {
      flows.push({
        id: `habit-${habit.id}`,
        title: `Habit: ${habit.name ?? 'Learning habit'}`,
        steps: [
          `Open the Habits workspace and locate "${habit.name ?? 'this habit'}".`,
          'Log today’s progress and confirm the streak updates.',
          'Refresh the dashboard to verify the habit appears in the focus module.',
        ],
      });
    });

    knowledgeEntries.slice(0, 2).forEach((entry) => {
      flows.push({
        id: `knowledge-${entry.id}`,
        title: `Knowledge: ${entry.title}`,
        steps: [
          `Review the knowledge note "${entry.title}" inside the knowledge base.`,
          'Update the summary or tags if anything changed during study.',
          'Link or verify related habits to keep recommendations aligned.',
        ],
      });
    });

    if (!flows.length) {
      flows.push({
        id: 'getting-started',
        title: 'Set up learning flows',
        steps: [
          'Create your first habit to track a daily learning commitment.',
          'Add a knowledge base note summarising what you are studying.',
          'Return here to run an integrated QA pass across your content.',
        ],
      });
    }

    return flows;
  }, [habits, knowledgeEntries]);

  useEffect(() => {
    setCompleted((prev) => {
      const validKeys = new Set();
      manualChecklist.forEach((flow) => {
        flow.steps.forEach((_, index) => {
          validKeys.add(`${flow.id}-${index}`);
        });
      });

      const next = new Set();
      prev.forEach((key) => {
        if (validKeys.has(key)) {
          next.add(key);
        }
      });
      return next;
    });
  }, [manualChecklist]);

  const activeSubjects = useMemo(() => {
    const subjectMap = new Map();

    habits.forEach((habit) => {
      const subject = habit.category || 'General focus';
      const current = subjectMap.get(subject) || { subject, habits: [], knowledge: [] };
      current.habits.push(habit);
      subjectMap.set(subject, current);
    });

    knowledgeEntries.forEach((entry) => {
      const subject = entry.category || 'General focus';
      const current = subjectMap.get(subject) || { subject, habits: [], knowledge: [] };
      current.knowledge.push(entry);
      subjectMap.set(subject, current);
    });

    return Array.from(subjectMap.values())
      .map((item) => ({
        ...item,
        habitCount: item.habits.length,
        knowledgeCount: item.knowledge.length,
      }))
      .sort((a, b) => b.habitCount + b.knowledgeCount - (a.habitCount + a.knowledgeCount))
      .slice(0, 4);
  }, [habits, knowledgeEntries]);

  const subjectOptions = useMemo(() => activeSubjects.map((item) => item.subject), [activeSubjects]);

  useEffect(() => {
    if (!secureSessionActive && subjectOptions.length > 0 && !subjectOption) {
      setSubjectOption(subjectOptions[0]);
    }
  }, [secureSessionActive, subjectOptions, subjectOption]);

  const habitStreaks = useMemo(() => {
    const logsByHabit = habitLogs.reduce((acc, log) => {
      if (!acc[log.habit_id]) {
        acc[log.habit_id] = [];
      }
      acc[log.habit_id].push(log);
      return acc;
    }, {});

    const computeStreak = (entries = []) => {
      const completedDates = new Set(entries.filter((log) => log.status === 'completed').map((log) => log.logged_on));
      let streak = 0;
      const cursor = new Date();
      cursor.setHours(0, 0, 0, 0);

      while (true) {
        const iso = cursor.toISOString().slice(0, 10);
        if (completedDates.has(iso)) {
          streak += 1;
          cursor.setDate(cursor.getDate() - 1);
        } else {
          break;
        }
      }

      return streak;
    };

    return habits
      .map((habit) => ({
        habitId: habit.id,
        name: habit.name,
        focusArea: habit.category || 'General focus',
        streak: computeStreak(logsByHabit[habit.id]),
      }))
      .sort((a, b) => b.streak - a.streak)
      .slice(0, 3);
  }, [habits, habitLogs]);

  const knowledgeHighlights = useMemo(() => knowledgeEntries.slice(0, 4), [knowledgeEntries]);

  const screenShareActive = useMemo(() => {
    if (!screenStream) {
      return false;
    }
    return screenStream.getTracks().some((track) => track.readyState === 'live');
  }, [screenStream]);

  const addSecurityEvent = useCallback((type, message) => {
    setSecurityEvents((prev) => {
      const next = [
        ...prev,
        {
          id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          type,
          message,
          timestamp: new Date().toISOString(),
        },
      ];
      return next.slice(-50);
    });
  }, []);

  useEffect(() => {
    if (!secureSessionActive) {
      return undefined;
    }
    if (typeof document === 'undefined') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        addSecurityEvent('focus', 'Browser tab hidden or focus lost');
      }
    };

    const handleWindowBlur = () => addSecurityEvent('focus', 'Window focus lost');

    const handleMouseLeave = (event) => {
      if (!event.relatedTarget) {
        addSecurityEvent('pointer', 'Pointer left the testing window');
      }
    };

    const handleContextMenu = (event) => {
      event.preventDefault();
      addSecurityEvent('pointer', 'Context menu attempted during secure session');
    };

    const handlePointerLockChange = () => {
      const locked = Boolean(document.pointerLockElement);
      setPointerLocked(locked);
      if (!locked) {
        addSecurityEvent('pointer', 'Pointer lock released');
      }
    };

    const handleFullscreenChange = () => {
      const active = Boolean(document.fullscreenElement);
      setFullscreenActive(active);
      if (!active) {
        addSecurityEvent('screen', 'Fullscreen exited');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('pointerlockchange', handlePointerLockChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    setPointerLocked(Boolean(document.pointerLockElement));
    setFullscreenActive(Boolean(document.fullscreenElement));

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('pointerlockchange', handlePointerLockChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [secureSessionActive, addSecurityEvent]);

  useEffect(() => {
    if (!screenStream) {
      return undefined;
    }

    const handleTrackEnded = () => {
      addSecurityEvent('screen', 'Screen capture stopped');
      setScreenStream(null);
    };

    screenStream.getTracks().forEach((track) => {
      track.addEventListener('ended', handleTrackEnded);
    });

    return () => {
      screenStream.getTracks().forEach((track) => {
        track.removeEventListener('ended', handleTrackEnded);
      });
    };
  }, [screenStream, addSecurityEvent]);

  useEffect(() => {
    if (!secureSessionActive) {
      setPointerLocked(false);
      setFullscreenActive(false);
    }
  }, [secureSessionActive]);

  const toggleStep = useCallback((id, index) => {
    const key = `${id}-${index}`;
    setCompleted((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const resetChecklist = useCallback(() => {
    setCompleted(new Set());
  }, []);

  const handleStartSession = useCallback(async () => {
    if (sessionPending) {
      return;
    }

    const chosenSubject = subjectOption || customSubject.trim();
    if (!chosenSubject) {
      setSessionError('Select or enter a subject before starting the secure session.');
      return;
    }

    setSessionError(null);
    setSessionPending(true);

    try {
      setSessionSubject(chosenSubject);
      setSessionStartedAt(new Date().toISOString());
      setSecurityEvents([]);
      setSecureSessionActive(true);
      addSecurityEvent('session', `Secure session started for ${chosenSubject}`);

      if (typeof document !== 'undefined') {
        try {
          if (!document.fullscreenElement && document.documentElement?.requestFullscreen) {
            await document.documentElement.requestFullscreen();
            setFullscreenActive(true);
            addSecurityEvent('screen', 'Fullscreen enforced');
          } else if (document.fullscreenElement) {
            setFullscreenActive(true);
          } else {
            addSecurityEvent('screen', 'Fullscreen not supported by this browser');
          }
        } catch (error) {
          addSecurityEvent('screen', `Fullscreen request blocked: ${error?.message || 'Unknown error'}`);
        }

        try {
          if (document.body?.requestPointerLock) {
            document.body.requestPointerLock();
          } else {
            addSecurityEvent('pointer', 'Pointer lock not supported by this browser');
          }
        } catch (error) {
          addSecurityEvent('pointer', `Pointer lock request failed: ${error?.message || 'Unknown error'}`);
        }
      }

      if (enableScreenShare) {
        if (screenShareSupported && typeof navigator !== 'undefined') {
          try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
            setScreenStream(stream);
            addSecurityEvent('screen', 'Screen capture started');
          } catch (error) {
            addSecurityEvent('screen', `Screen capture denied: ${error?.message || 'Permission denied'}`);
          }
        } else {
          addSecurityEvent('screen', 'Screen capture not supported on this device');
        }
      }
    } finally {
      setSessionPending(false);
    }
  }, [sessionPending, subjectOption, customSubject, enableScreenShare, screenShareSupported, addSecurityEvent]);

  const handleEndSession = useCallback(async () => {
    if (sessionPending) {
      return;
    }

    setSessionPending(true);

    try {
      setSecureSessionActive(false);
      setSessionSubject('');
      setSessionStartedAt(null);
      setCustomSubject('');
      if (subjectOptions.length > 0) {
        setSubjectOption(subjectOptions[0]);
      } else {
        setSubjectOption('');
      }

      if (typeof document !== 'undefined') {
        try {
          if (document.exitPointerLock) {
            document.exitPointerLock();
          }
        } catch (error) {
          addSecurityEvent('pointer', `Pointer lock release failed: ${error?.message || 'Unknown error'}`);
        }

        try {
          if (document.fullscreenElement && document.exitFullscreen) {
            await document.exitFullscreen();
          }
        } catch (error) {
          addSecurityEvent('screen', `Fullscreen exit failed: ${error?.message || 'Unknown error'}`);
        }
      }

      if (screenStream) {
        screenStream.getTracks().forEach((track) => track.stop());
        setScreenStream(null);
      }

      addSecurityEvent('session', 'Secure session ended');
    } finally {
      setPointerLocked(false);
      setFullscreenActive(false);
      setSessionPending(false);
    }
  }, [sessionPending, subjectOptions, screenStream, addSecurityEvent]);

  const securityIndicators = useMemo(
    () => [
      {
        key: 'fullscreen',
        label: 'Fullscreen guard',
        active: fullscreenActive,
        activeText: 'Browser locked to fullscreen.',
        inactiveText: 'Not in fullscreen – the attempt is flagged.',
        icon: fullscreenActive ? 'Maximize2' : 'Maximize',
      },
      {
        key: 'pointer',
        label: 'Pointer lock',
        active: pointerLocked,
        activeText: 'Pointer locked inside the workspace.',
        inactiveText: 'Pointer can leave the screen – watch for focus alerts.',
        icon: pointerLocked ? 'MousePointerClick' : 'MousePointer',
      },
      {
        key: 'screen',
        label: 'Screen capture',
        active: screenShareActive,
        activeText: 'Screen capture running for invigilator review.',
        inactiveText: enableScreenShare
          ? 'Screen capture disabled or permission denied.'
          : 'Screen capture not required for this session.',
        icon: screenShareActive ? 'Cast' : 'Monitor',
      },
    ],
    [fullscreenActive, pointerLocked, screenShareActive, enableScreenShare]
  );

  const securityLog = useMemo(() => securityEvents.slice().reverse(), [securityEvents]);

  if (initializing) {
    return (
      <AuthenticationGuard isAuthenticated={true}>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <SmartLoadingBuffer
              className="min-h-[60vh]"
              title="Preparing testing workspace"
              description="Syncing your learning data and secure controls."
            />
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 space-y-8">
            {isLoadingData ? (
              <SmartLoadingBuffer
                className="min-h-[40vh]"
                title="Loading personalised testing workspace"
                description="Pulling your habits, knowledge notes, and recent streaks."
              />
            ) : (
              <>
                <section className="space-y-3">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wide">
                    <Icon name="TestTube" size={14} />
                    <span>Testing workspace</span>
                  </div>
                  <h1 className="text-3xl font-semibold text-foreground leading-tight">Secure assessments aligned to your study plan</h1>
                  <p className="text-muted-foreground max-w-3xl">
                    Launch a focused testing session, monitor integrity signals in real time, and walk through manual QA steps fed directly by your habits and knowledge base.
                  </p>
                </section>

                {loadError && (
                  <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                    Unable to load the latest learning data. Refresh or retry after a moment.
                  </div>
                )}

                <section className="bg-card border border-border rounded-2xl p-6 space-y-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <Icon name="ShieldCheck" size={24} className="text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-xl font-semibold text-foreground">Secure testing session</h2>
                        <p className="text-sm text-muted-foreground max-w-md">
                          Choose the subject focus, start the lock-down sequence, and track any suspicious behaviour while the session runs.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Screen capture</span>
                      <button
                        type="button"
                        onClick={() => setEnableScreenShare((prev) => !prev)}
                        className={`w-12 h-6 rounded-full transition flex items-center px-1 ${
                          enableScreenShare ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded-full bg-background shadow transform transition ${
                            enableScreenShare ? 'translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {!screenShareSupported && enableScreenShare && (
                    <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
                      Screen capture is not supported in this browser. The session will continue without it.
                    </div>
                  )}

                  {sessionError && (
                    <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                      {sessionError}
                    </div>
                  )}

                  {secureSessionActive ? (
                    <div className="space-y-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Subject focus</p>
                          <p className="text-lg font-semibold text-foreground">{sessionSubject}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Session started</p>
                          <p className="text-lg font-semibold text-foreground">{sessionStartedAt ? formatTimestamp(sessionStartedAt) : '—'}</p>
                        </div>
                        <Button
                          variant="destructive"
                          iconName="Square"
                          onClick={handleEndSession}
                          loading={sessionPending}
                          className="md:w-auto w-full"
                        >
                          End secure session
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {securityIndicators.map((indicator) => (
                          <div
                            key={indicator.key}
                            className={`rounded-xl border p-4 space-y-2 ${
                              indicator.active ? 'border-success/60 bg-success/10' : 'border-warning/40 bg-warning/10'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <Icon name={indicator.icon} size={18} className={indicator.active ? 'text-success' : 'text-warning'} />
                              <span className="text-sm font-semibold text-foreground">{indicator.label}</span>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {indicator.active ? indicator.activeText : indicator.inactiveText}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex flex-col space-y-2">
                          <span className="text-sm font-medium text-foreground">Select learning subject</span>
                          <select
                            value={subjectOption}
                            onChange={(event) => setSubjectOption(event.target.value)}
                            className="rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                          >
                            <option value="">— Choose from active subjects —</option>
                            {subjectOptions.map((subject) => (
                              <option key={subject} value={subject}>
                                {subject}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="flex flex-col space-y-2">
                          <span className="text-sm font-medium text-foreground">Or type a custom subject</span>
                          <input
                            type="text"
                            value={customSubject}
                            onChange={(event) => setCustomSubject(event.target.value)}
                            placeholder="e.g. Calculus revision"
                            className="rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                        </label>
                      </div>

                      <div className="bg-muted/20 border border-border rounded-lg p-4 text-sm text-muted-foreground space-y-2">
                        <div className="flex items-center space-x-2">
                          <Icon name="Shield" size={16} className="text-muted-foreground" />
                          <span className="font-medium text-foreground">Once started, Cogito will:</span>
                        </div>
                        <ul className="space-y-1">
                          <li>• Request fullscreen mode and pointer lock to discourage navigating away.</li>
                          <li>• Optionally record the screen for proctor review (browser permissions apply).</li>
                          <li>• Log focus changes, pointer exits, and other suspicious events below.</li>
                        </ul>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                        <Button
                          iconName="Lock"
                          onClick={handleStartSession}
                          loading={sessionPending}
                          className="sm:w-auto w-full"
                        >
                          Start secure session
                        </Button>
                        <Button
                          variant="ghost"
                          iconName="RefreshCw"
                          onClick={loadWorkspaceData}
                          className="sm:w-auto w-full"
                        >
                          Refresh learning data
                        </Button>
                      </div>
                    </div>
                  )}
                </section>

                <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Layers" size={18} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Subjects in rotation</h2>
                        <p className="text-xs text-muted-foreground">Derived from your active habits and knowledge notes.</p>
                      </div>
                    </div>
                    {activeSubjects.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No subjects detected yet. Create a habit or knowledge note to seed this list.</p>
                    ) : (
                      <ul className="space-y-3 text-sm text-muted-foreground">
                        {activeSubjects.map((item) => (
                          <li key={item.subject} className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-foreground">{item.subject}</p>
                              <p className="text-xs">{item.habitCount} habits • {item.knowledgeCount} knowledge entries</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="ArrowRight"
                              onClick={() => setSubjectOption(item.subject)}
                            >
                              Focus
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                        <Icon name="Flame" size={18} className="text-success" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Top streaks</h2>
                        <p className="text-xs text-muted-foreground">Keep momentum to lower test anxiety.</p>
                      </div>
                    </div>
                    {habitStreaks.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No recorded streaks yet. Log a habit to populate this panel.</p>
                    ) : (
                      <ul className="space-y-3 text-sm text-muted-foreground">
                        {habitStreaks.map((streak) => (
                          <li key={streak.habitId} className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/10 px-3 py-2">
                            <div>
                              <p className="font-semibold text-foreground">{streak.name || 'Habit'}</p>
                              <p className="text-xs">{streak.focusArea}</p>
                            </div>
                            <span className="text-xs font-semibold text-success uppercase tracking-wide">{streak.streak} day streak</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>

                <section className="bg-card border border-border rounded-2xl p-6 space-y-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Icon name="ClipboardCheck" size={24} className="text-primary" />
                    </div>
                    <div className="space-y-2">
                      <h2 className="text-xl font-semibold text-foreground">Manual run checklist</h2>
                      <p className="text-sm text-muted-foreground">
                        Tick off each scenario as you confirm behaviour across your learning flows. Reset whenever you start a new regression pass.
                      </p>
                    </div>
                    <Button variant="ghost" iconName="RefreshCw" className="ml-auto" onClick={resetChecklist}>
                      Reset tracker
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {manualChecklist.map((flow) => (
                      <div key={flow.id} className="rounded-xl border border-border/70 bg-muted/10 p-4 space-y-3">
                        <div className="flex items-center space-x-2">
                          <Icon name="ListChecks" size={18} className="text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">{flow.title}</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          {flow.steps.map((step, index) => {
                            const key = `${flow.id}-${index}`;
                            const isDone = completed.has(key);
                            return (
                              <li key={key}>
                                <button
                                  type="button"
                                  onClick={() => toggleStep(flow.id, index)}
                                  className={`w-full flex items-start space-x-2 rounded-lg border px-3 py-2 text-left transition ${
                                    isDone
                                      ? 'border-success/50 bg-success/10 text-success'
                                      : 'border-border bg-background hover:border-primary/40 hover:text-foreground'
                                  }`}
                                >
                                  <span
                                    className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                                      isDone ? 'border-success bg-success text-success-foreground' : 'border-border'
                                    }`}
                                  >
                                    {isDone ? <Icon name="Check" size={12} /> : ''}
                                  </span>
                                  <span className="flex-1 text-sm leading-relaxed">{step}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="bg-muted/20 border border-dashed border-border rounded-2xl p-6 space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="BookOpen" size={18} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Knowledge highlights</h2>
                      <p className="text-xs text-muted-foreground">Revisit these notes before or after each secure attempt.</p>
                    </div>
                  </div>
                  {knowledgeHighlights.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No notes yet. Add an entry in the knowledge base to surface it here.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {knowledgeHighlights.map((entry) => (
                        <div key={entry.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                          <div className="flex items-center space-x-2">
                            <Icon name="FileText" size={16} className="text-primary" />
                            <h3 className="text-sm font-semibold text-foreground truncate">{entry.title}</h3>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-3">{entry.summary || 'No summary provided yet.'}</p>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{entry.category}</span>
                            <span>{entry.difficulty}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="bg-card border border-border rounded-2xl p-6 space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                      <Icon name="Activity" size={18} className="text-warning" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Security event log</h2>
                      <p className="text-sm text-muted-foreground">Track focus changes, pointer exits, and screen capture state during the session.</p>
                    </div>
                  </div>
                  {securityLog.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No events yet. Start a secure session to populate the log.</p>
                  ) : (
                    <ul className="space-y-2 max-h-56 overflow-y-auto pr-1 text-xs text-muted-foreground">
                      {securityLog.map((event) => (
                        <li
                          key={event.id}
                          className="flex items-start space-x-2 rounded-lg border border-border/50 bg-muted/10 px-3 py-2"
                        >
                          <span className="font-semibold text-foreground">{formatTimestamp(event.timestamp)}</span>
                          <span className="flex-1 leading-relaxed">{event.message}</span>
                          <span className="uppercase tracking-wide text-[10px] text-muted-foreground/70">{event.type}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default TestingPage;